// src/components/WhatsAppFab.jsx
import { useMemo, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Fab, Tooltip, Box, useMediaQuery, useTheme } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

function buildWhatsAppLink(phone, message) {
  const base = `https://wa.me/${phone}`;
  const query = message ? `?text=${encodeURIComponent(message)} ` : "";
  return `${base}${query}`;
}

const LS_KEY = "whatsapp_fab_pos_v1"; // chave para persistir posição no localStorage

export default function WhatsAppFab({
  phone,
  defaultMessage = "Olá! Preciso de ajuda.",
  position = { bottom: 24, right: 24 },
  hideOnSmallScreens = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const href = useMemo(
    () => buildWhatsAppLink(phone, defaultMessage),
    [phone, defaultMessage]
  );

  // ====== CONFIGURAÇÃO DO ARRASTO (apenas mobile) ======
  const edgePadding = 16; // distância mínima das bordas
  const size = 56; // tamanho do FAB

  const [pos, setPos] = useState(() => {
    if (typeof window === "undefined") return { x: 0, y: 0 };

    // tenta recuperar a última posição
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved) return JSON.parse(saved);
    } catch { /* intentional */ }

    // posição padrão: canto inferior direito
    const w = window.innerWidth;
    const h = window.innerHeight;
    return { x: w - size - edgePadding, y: h - size - edgePadding };
  });

  // persiste posição apenas quando estiver em mobile
  useEffect(() => {
    if (!isMobile) return;
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(pos));
    } catch { /* intentional */ }
  }, [pos, isMobile]);

  // ajusta para não sair da tela ao redimensionar/orientação mudar
  useEffect(() => {
    if (!isMobile) return;
    function clampToViewport() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setPos((p) => ({
        x: Math.min(Math.max(edgePadding, p.x), w - size - edgePadding),
        y: Math.min(Math.max(edgePadding, p.y), h - size - edgePadding),
      }));
    }
    window.addEventListener("resize", clampToViewport);
    window.addEventListener("orientationchange", clampToViewport);
    return () => {
      window.removeEventListener("resize", clampToViewport);
      window.removeEventListener("orientationchange", clampToViewport);
    };
  }, [isMobile]);

  // Lógica de arrasto com Pointer Events
  const wrapperRef = useRef(null);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });
  const originRef = useRef({ x: pos.x, y: pos.y });
  const pointerIdRef = useRef(null); // [NOVO]

  function onPointerDown(e) {
    if (!isMobile) return;
    draggingRef.current = true;
    movedRef.current = false;
    originRef.current = { x: pos.x, y: pos.y };
    startRef.current = { x: e.clientX, y: e.clientY };
    pointerIdRef.current = e.pointerId; // [NOVO]
    wrapperRef.current?.setPointerCapture?.(e.pointerId);

    const el = wrapperRef.current;
    if (el?.setPointerCapture && typeof pointerIdRef.current === "number") {
      try {
        el.setPointerCapture(pointerIdRef.current);
      } catch { /* intentional */ }
    }
    if (el?.style) el.style.touchAction = "none";
  }

  function onPointerMove(e) {
    if (!isMobile || !draggingRef.current) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;

    const w = window.innerWidth;
    const h = window.innerHeight;

    const nextX = Math.min(
      Math.max(edgePadding, originRef.current.x + dx),
      w - size - edgePadding
    );
    const nextY = Math.min(
      Math.max(edgePadding, originRef.current.y + dy),
      h - size - edgePadding
    );

    setPos({ x: nextX, y: nextY });
  }

  function cleanupPointerCapture() {
    // [NOVO]
    const el = wrapperRef.current;
    const pid = pointerIdRef.current;
    if (el?.releasePointerCapture && typeof pid === "number") {
      try {
        if (el.hasPointerCapture?.(pid)) {
          el.releasePointerCapture(pid); // libera só se estiver capturado
        }
      } catch { /* intentional */ }
    }
    pointerIdRef.current = null;
    if (el?.style) el.style.touchAction = "auto";
  }

  function onPointerUp(e) {
    if (!isMobile || !draggingRef.current) return;
    draggingRef.current = false;
    cleanupPointerCapture(e); // [NOVO]
  }

  function onPointerCancel(e) {
    // [NOVO]
    draggingRef.current = false;
    movedRef.current = false;
    cleanupPointerCapture(e);
  }

  // Clique: só abre o link se não houve arrasto
  function onClick(e) {
    if (draggingRef.current || movedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    window.open(href, "_blank", "noopener,noreferrer");
  }

  // ====== RENDER ======
  if (isMobile) {
    if (hideOnSmallScreens) return null;

    // Mobile: arrastável (top/left com Box wrapper)
    return (
      <Box
        ref={wrapperRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        sx={{
          position: "fixed",
          zIndex: (t) => t.zIndex.tooltip + 1,
          top: pos.y,
          left: pos.x,
          width: size,
          height: size,
          // melhora a experiência de arrasto em mobile
          touchAction: "none",
        }}
      >
        <Tooltip title="Fale conosco pelo WhatsApp" placement="left">
          <Fab
            color="success"
            aria-label="Contato via WhatsApp"
            onClick={onClick}
            sx={{
              width: size,
              height: size,
              minHeight: size,
              boxShadow: 6,
              bgcolor: "#25D366",
              color: "#fff",
              "&:hover": { bgcolor: "#1EBE57" },
            }}
          >
            <WhatsAppIcon />
          </Fab>
        </Tooltip>
      </Box>
    );
  }

  // Desktop: fixo com bottom/right (sem arrasto)
  return (
    <Tooltip title="Fale conosco pelo WhatsApp" placement="left">
      <Fab
        aria-label="Contato via WhatsApp"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          position: "fixed",
          zIndex: (t) => t.zIndex.tooltip + 1,
          bottom: position.bottom ?? 24,
          right: position.right,
          left: position.left,
          boxShadow: 6,
          bgcolor: "#25D366",
          color: "#fff",
          "&:hover": { bgcolor: "#1EBE57" },
        }}
      >
        <WhatsAppIcon />
      </Fab>
    </Tooltip>
  );
}

WhatsAppFab.propTypes = {
  phone: PropTypes.string.isRequired,
  defaultMessage: PropTypes.string,
  position: PropTypes.shape({
    bottom: PropTypes.number,
    right: PropTypes.number,
    left: PropTypes.number,
  }),
  hideOnSmallScreens: PropTypes.bool,
};
